/**
 * ChecklistReportingPage.jsx
 * 
 * Centre Manager Daily Checklist Reporting Component
 * 
 * Features:
 * - Calendar view per property showing checklist submission status
 * - Month/year navigation
 * - Property selection dropdown
 * - Visual indicators:
 *   - Green: Checklist submitted
 *   - Light red: Checklist missed
 *   - Neutral: Sundays and public holidays (not counted as missed)
 *   - Gray: Future dates
 * - Ability to add reasons for missed checklists
 * - Displays public holiday names
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import '@/styles/ChecklistReportingStyles.scss';
import { 
    FaCalendarAlt, 
    FaChevronLeft, 
    FaChevronRight,
    FaCheckCircle,
    FaTimesCircle,
    FaInfoCircle,
    FaEdit,
    FaExclamationTriangle,
    FaToggleOn,
    FaToggleOff,
    FaClipboardList
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * Modal component for entering/viewing missed checklist reasons
 */
const MissedReasonModal = ({ isOpen, onClose, date, propertyName, existingReason, providedByUserName, providedDate, onSave }) => {
    const [reason, setReason] = useState(existingReason || '');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setReason(existingReason || '');
    }, [existingReason]);

    const handleSave = async () => {
        if (!reason.trim()) {
            toast.error('Please provide a reason');
            return;
        }

        setSaving(true);
        try {
            await onSave(date, reason);
            onClose();
        } catch (error) {
            Sentry.captureException(error);
            // Error already handled in onSave
            console.log(error);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="modal-title">
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 id="modal-title">Missed Checklist Reason</h3>
                    <button className="close-btn" onClick={onClose} aria-label="Close modal">×</button>
                </div>
                <div className="modal-body">
                    <p><strong>Property:</strong> {propertyName}</p>
                    <p><strong>Date:</strong> {date}</p>
                    {providedByUserName && (
                        <div className="reason-audit-info">
                            <p><strong>Provided by:</strong> {providedByUserName}</p>
                            {providedDate && (
                                <p><strong>Date provided:</strong> {new Date(providedDate).toLocaleString()}</p>
                            )}
                        </div>
                    )}
                    <label htmlFor="reason">Reason for missing checklist:</label>
                    <textarea
                        id="reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={4}
                        placeholder="Please provide a reason for missing the checklist..."
                        disabled={saving}
                        aria-required="true"
                    />
                </div>
                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button className="btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

MissedReasonModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    date: PropTypes.string,
    propertyId: PropTypes.number,
    propertyName: PropTypes.string,
    existingReason: PropTypes.string,
    providedByUserName: PropTypes.string,
    providedDate: PropTypes.string,
    onSave: PropTypes.func.isRequired
};

/**
 * Main Checklist Reporting Page Component
 * @param {string} initialTab - Which tab to show initially ('checklists' or 'exceptions')
 * @param {boolean} showExceptionTab - Whether to show the Exception Report tab (permission-controlled)
 * @param {boolean} showChecklistTab - Whether to show the Daily Checklists tab (permission-controlled)
 */
const ChecklistReportingPage = ({ initialTab = 'checklists', showExceptionTab = true, showChecklistTab = true }) => {
    const { user, apiFetch } = useAuth();
    const navigate = useNavigate();
    const [properties, setProperties] = useState([]);
    const [selectedPropertyId, setSelectedPropertyId] = useState(null);
    const [calendarData, setCalendarData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [exceptionData, setExceptionData] = useState(null);
    const [exceptionLoading, setExceptionLoading] = useState(false);
    const [includeExcused, setIncludeExcused] = useState(true);

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    /**
     * Load list of accessible properties for the current user
     */
    const loadAccessibleProperties = useCallback(async () => {
        try {
            // Use existing Permissions endpoint (same as CMDailyChecklist)
            const response = await apiFetch(`/api/Permissions/my-accessible-properties/${user.userID}`);
            const data = await response.json();

            if (data.success) {
                // Handle both unrestricted and restricted access
                const props = data.data?.unrestricted
                    ? [] // If unrestricted but no properties returned, fetch all
                    : data.data?.properties || [];

                // If unrestricted, fetch all properties
                if (data.data?.unrestricted) {
                    const allPropsResponse = await apiFetch('/api/Property/all');
                    const allPropsData = await allPropsResponse.json();
                    if (allPropsData.success && allPropsData.data) {
                        const activeProps = [...allPropsData.data.filter(p => p.active)]
                            .sort((a, b) => (a.propertyName || '').localeCompare(b.propertyName || ''));
                        setProperties(activeProps);
                        // Auto-select first property if available
                        if (activeProps.length > 0 && !selectedPropertyId) {
                            setSelectedPropertyId(activeProps[0].propertyID);
                        }
                    }
                } else {
                    const sortedProps = [...props].sort((a, b) => (a.propertyName || '').localeCompare(b.propertyName || ''));
                    setProperties(sortedProps);
                    // Auto-select first property if available
                    if (sortedProps.length > 0 && !selectedPropertyId) {
                        setSelectedPropertyId(sortedProps[0].propertyID);
                    }
                }
            }
        } catch (error) {
            Sentry.captureException(error);
            // Log error for debugging but don't expose to console in production
            if (process.env.NODE_ENV === 'development') {
                console.error('Error loading properties:', error);
            }
            toast.error('Failed to load properties');
        }
    }, [apiFetch, user?.userID, selectedPropertyId]);

    /**
     * Load calendar data for selected property and date range
     */
    const loadCalendarData = useCallback(async () => {
        if (!selectedPropertyId) return;

        setLoading(true);
        try {
            const url = `/api/ChecklistReporting/calendar?propertyId=${selectedPropertyId}&year=${selectedYear}&month=${selectedMonth}`;
            const response = await apiFetch(url);
            const data = await response.json();

            if (data.success) {
                setCalendarData(data.data);
            } else {
                toast.error(data.message || 'Failed to load calendar data');
            }
        } catch (error) {
            Sentry.captureException(error);
            // Log error for debugging but don't expose to console in production
            if (process.env.NODE_ENV === 'development') {
                console.error('Error loading calendar data:', error);
            }
            toast.error('Failed to load calendar data');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, selectedPropertyId, selectedYear, selectedMonth]); // Updated dependencies

    /**
     * Load exception report data for the selected month
     */
    const loadExceptionReport = useCallback(async () => {
        setExceptionLoading(true);
        try {
            const url = `/api/ChecklistReporting/exception-report?year=${selectedYear}&month=${selectedMonth}&includeExcused=${includeExcused}`;
            const response = await apiFetch(url);
            const data = await response.json();

            if (data.success) {
                setExceptionData(data.data);
            } else {
                toast.error(data.message || 'Failed to load exception report');
            }
        } catch (error) {
            Sentry.captureException(error);
            if (process.env.NODE_ENV === 'development') {
                console.error('Error loading exception report:', error);
            }
            toast.error('Failed to load exception report');
        } finally {
            setExceptionLoading(false);
        }
    }, [apiFetch, selectedYear, selectedMonth, includeExcused]);

    // Load accessible properties on mount
    useEffect(() => {
        loadAccessibleProperties();
    }, [loadAccessibleProperties]); // Depend on the memoized callback to avoid stale closures

    // Load calendar data when property or date selection changes
    useEffect(() => {
        if (selectedPropertyId) {
            loadCalendarData();
        }
    }, [selectedPropertyId, selectedYear, selectedMonth, loadCalendarData]); // Include callback to prevent stale closures

    // Load exception report when tab is active or filters change
    useEffect(() => {
        if (activeTab === 'exceptions') {
            loadExceptionReport();
        }
    }, [activeTab, selectedYear, selectedMonth, includeExcused, loadExceptionReport]);

    /**
     * Navigate to previous month
     */
    const goToPreviousMonth = () => {
        if (selectedMonth === 1) {
            setSelectedMonth(12);
            setSelectedYear(selectedYear - 1);
        } else {
            setSelectedMonth(selectedMonth - 1);
        }
    };

    /**
     * Navigate to next month
     */
    const goToNextMonth = () => {
        if (selectedMonth === 12) {
            setSelectedMonth(1);
            setSelectedYear(selectedYear + 1);
        } else {
            setSelectedMonth(selectedMonth + 1);
        }
    };

    /**
     * Handle clicking on a day to add/edit missed reason
     */
    const handleDayClick = (day) => {
        // If submitted and has an ActionItemId, navigate to the action item
        if (day.isSubmitted && day.actionItemId) {
            navigate(`/action-items/${day.actionItemId}`);
            return;
        }
        // Only allow editing reasons for missed days (not submitted, not future, not Sunday/holiday)
        if (!day.isSubmitted && !day.isFutureDate && !day.isSunday && !day.isPublicHoliday) {
            setSelectedDay(day);
            setModalOpen(true);
        } else if (day.missedReason) {
            // Allow viewing existing reasons even for submitted days
            setSelectedDay(day);
            setModalOpen(true);
        }
    };

    /**
     * Save missed checklist reason
     */
    const saveMissedReason = async (date, reason) => {
        try {
            const response = await apiFetch(
                `/api/ChecklistReporting/missed-reason`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        propertyID: selectedPropertyId,
                        missedDate: date,
                        reason: reason,
                        userID: user?.userID || 0
                    })
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                toast.error(errorData.message || 'Failed to save reason');
                return;
            }

            const data = await response.json();
            if (data.success) {
                toast.success('Reason saved successfully');
                await loadCalendarData();
                setModalOpen(false);
                setSelectedDay(null);
            } else {
                toast.error(data.message || 'Failed to save reason');
            }
        } catch (error) {
            Sentry.captureException(error);
            if (process.env.NODE_ENV === 'development') {
                console.error('Error saving reason:', error);
            }
            toast.error('Failed to save reason');
            throw error;
        }
    };

    /**
     * Get CSS class for a calendar day based on its status
     */
    const getDayClassName = (day) => {
        const classes = ['calendar-day'];

        if (day.isFutureDate) {
            classes.push('future');
        } else if (day.isSunday || day.isPublicHoliday) {
            classes.push('holiday');
        } else if (day.isSubmitted) {
            classes.push('submitted');
        } else if (!day.isSubmitted && day.missedReason) {
            classes.push('excused'); // <-- Orange for missed with reason
        } else {
            classes.push('missed');
        }

        // Add clickable class if day can have a reason added or has an action item
        if ((!day.isSubmitted && !day.isFutureDate && !day.isSunday && !day.isPublicHoliday) || day.missedReason || (day.isSubmitted && day.actionItemId)) {
            classes.push('clickable');
        }

        return classes.join(' ');
    };

    /**
     * Render calendar grid
     */
    const renderCalendar = () => {
        if (!calendarData || !calendarData.days) return null;

        const days = calendarData.days;
        const firstDayOfMonth = days[0]?.dayOfWeek || 0;

        // Add empty cells for days before the first of the month
        const calendarCells = [];
        for (let i = 0; i < firstDayOfMonth; i++) {
            calendarCells.push(<div key={`empty-${i}`} className="calendar-day empty" />);
        }

        // Add day cells
        days.forEach((day) => {
            const dayNumber = new Date(day.date).getDate();
            const hasReason = !!day.missedReason;

            calendarCells.push(
                <div
                    key={day.date}
                    className={getDayClassName(day)}
                    onClick={() => handleDayClick(day)}
                    title={day.holidayName || (day.isSubmitted && day.actionItemId ? 'Click to view Action Item' : '')}
                >
                    <div className="day-number">{dayNumber}</div>
                    {day.isSubmitted && <FaCheckCircle className="status-icon submitted-icon" />}
                    {!day.isSubmitted && !day.isFutureDate && !day.isSunday && !day.isPublicHoliday && !hasReason && (
                        <FaTimesCircle className="status-icon missed-icon" />
                    )}
                    {day.isPublicHoliday && <FaInfoCircle className="status-icon holiday-icon" />}
                    {hasReason && (
                        <div className="excuse-text">
                            <span className="excuse-provider">
                                {day.missedReasonProvidedByUserName || 'Unknown'}
                            </span>
                            <span className="excuse-reason">{day.missedReason}</span>
                        </div>
                    )}
                    {day.holidayName && <div className="holiday-name">{day.holidayName}</div>}
                </div>
            );
        });

        return calendarCells;
    };

    /**
     * Calculate statistics
     */
    const getStats = () => {
        if (!calendarData || !calendarData.days) {
            return { submitted: 0, missed: 0, excused: 0, total: 0 };
        }

        const workdays = calendarData.days.filter(
            d => !d.isFutureDate && !d.isSunday && !d.isPublicHoliday
        );

        const submitted = workdays.filter(d => d.isSubmitted).length;
        const excused = workdays.filter(d => !d.isSubmitted && d.missedReason).length;
        const missed = workdays.filter(d => !d.isSubmitted && !d.missedReason).length;
        const total = workdays.length;

        return { submitted, missed, excused, total };
    };

    const stats = getStats();
    const selectedProperty = properties.find(p => p.propertyID === selectedPropertyId);

    return (
        <div className="checklist-reporting-page">
            <div className="page-header">
                <div className="header-title">
                    <FaCalendarAlt className="title-icon" />
                    <h1>Centre Manager Daily Checklist Reporting</h1>
                </div>
                <p className="subtitle">
                    Track checklist submission compliance across properties
                </p>
            </div>

            {/* Tab Navigation - only show when user has access to both tabs */}
            {showChecklistTab && showExceptionTab && (
                <div className="tab-navigation">
                    <button
                        className={`tab-btn ${activeTab === 'checklists' ? 'active' : ''}`}
                        onClick={() => setActiveTab('checklists')}
                    >
                        <FaClipboardList /> Daily Checklists
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'exceptions' ? 'active' : ''}`}
                        onClick={() => setActiveTab('exceptions')}
                    >
                        <FaExclamationTriangle /> Exception Report
                    </button>
                </div>
            )}

            {/* ===== DAILY CHECKLISTS TAB ===== */}
            {activeTab === 'checklists' && (
                <>
                    <div className="controls-section">
                        <div className="property-selector">
                            <label htmlFor="property-select">Select Property:</label>
                            <select
                                id="property-select"
                                value={selectedPropertyId || ''}
                                onChange={(e) => setSelectedPropertyId(parseInt(e.target.value))}
                            >
                                <option value="">Select a property...</option>
                                {properties.map(prop => (
                                    <option key={prop.propertyID} value={prop.propertyID}>
                                        {prop.propertyName} ({prop.propertyCode})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="month-navigator">
                            <button 
                                className="nav-btn" 
                                onClick={goToPreviousMonth}
                                aria-label="Previous month"
                            >
                                <FaChevronLeft />
                            </button>
                            <span className="current-month" aria-live="polite">
                                {monthNames[selectedMonth - 1]} {selectedYear}
                            </span>
                            <button 
                                className="nav-btn" 
                                onClick={goToNextMonth}
                                aria-label="Next month"
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    </div>

                    {selectedPropertyId && (
                        <div className="stats-section">
                            <div className="stat-card submitted">
                                <FaCheckCircle className="stat-icon" />
                                <div className="stat-content">
                                    <div className="stat-value">{stats.submitted}</div>
                                    <div className="stat-label">Submitted</div>
                                </div>
                            </div>
                            <div className="stat-card missed">
                                <FaTimesCircle className="stat-icon" />
                                <div className="stat-content">
                                    <div className="stat-value">{stats.missed}</div>
                                    <div className="stat-label">Missed</div>
                                </div>
                            </div>
                            <div className="stat-card excused">
                                <FaEdit className="stat-icon" />
                                <div className="stat-content">
                                    <div className="stat-value">{stats.excused}</div>
                                    <div className="stat-label">Excused</div>
                                </div>
                            </div>
                            <div className="stat-card total">
                                <FaCalendarAlt className="stat-icon" />
                                <div className="stat-content">
                                    <div className="stat-value">{stats.total}</div>
                                    <div className="stat-label">Workdays</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {loading && (
                        <div className="loading-container">
                            <div className="spinner" />
                            <p>Loading calendar data...</p>
                        </div>
                    )}

                    {!loading && selectedPropertyId && calendarData && (
                        <div className="calendar-container">
                            <div className="calendar-header">
                                {dayNames.map(day => (
                                    <div key={day} className="calendar-header-day">
                                        {day}
                                    </div>
                                ))}
                            </div>
                            <div className="calendar-grid">
                                {renderCalendar()}
                            </div>

                            <div className="legend">
                                <div className="legend-item">
                                    <div className="legend-color submitted" />
                                    <span>Submitted</span>
                                </div>
                                <div className="legend-item">
                                    <div className="legend-color missed" />
                                    <span>Missed</span>
                                </div>
                                <div className="legend-item">
                                    <div className="legend-color holiday" />
                                    <span>Sunday/Holiday</span>
                                </div>
                                <div className="legend-item">
                                    <div className="legend-color future" />
                                    <span>Future Date</span>
                                </div>
                                <div className="legend-item">
                                    <div className="legend-color excused" />
                                    <span>Missed (Reason Provided)</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {!loading && !selectedPropertyId && (
                        <div className="empty-state">
                            <FaCalendarAlt className="empty-icon" />
                            <p>Please select a property to view checklist reports</p>
                        </div>
                    )}
                </>
            )}

            {/* ===== EXCEPTION REPORT TAB ===== */}
            {activeTab === 'exceptions' && (
                <>
                    <div className="controls-section">
                        <div className="exception-toggle">
                            <button
                                className="toggle-btn"
                                onClick={() => setIncludeExcused(!includeExcused)}
                                title={includeExcused ? 'Click to hide days where a reason was provided' : 'Click to show days where a reason was provided'}
                            >
                                {includeExcused ? <FaToggleOn className="toggle-icon on" /> : <FaToggleOff className="toggle-icon off" />}
                                <span>Include days where a reason was provided</span>
                            </button>
                        </div>

                        <div className="month-navigator">
                            <button 
                                className="nav-btn" 
                                onClick={goToPreviousMonth}
                                aria-label="Previous month"
                            >
                                <FaChevronLeft />
                            </button>
                            <span className="current-month" aria-live="polite">
                                {monthNames[selectedMonth - 1]} {selectedYear}
                            </span>
                            <button 
                                className="nav-btn" 
                                onClick={goToNextMonth}
                                aria-label="Next month"
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    </div>

                    {exceptionLoading && (
                        <div className="loading-container">
                            <div className="spinner" />
                            <p>Loading exception report...</p>
                        </div>
                    )}

                    {!exceptionLoading && exceptionData && (
                        <div className="exception-report">
                            <div className="exception-summary">
                                <div className="summary-card">
                                    <div className="summary-value">{exceptionData.totalProperties}</div>
                                    <div className="summary-label">Total Properties</div>
                                </div>
                                <div className="summary-card alert">
                                    <div className="summary-value">{exceptionData.propertiesWithExceptions}</div>
                                    <div className="summary-label">With Exceptions</div>
                                </div>
                                <div className="summary-card success">
                                    <div className="summary-value">{exceptionData.totalProperties - exceptionData.propertiesWithExceptions}</div>
                                    <div className="summary-label">Fully Compliant</div>
                                </div>
                            </div>

                            {exceptionData.properties.length === 0 ? (
                                <div className="empty-state">
                                    <FaCheckCircle className="empty-icon" style={{ color: '#10b981' }} />
                                    <p>No exceptions found for {exceptionData.monthName} {exceptionData.year}</p>
                                </div>
                            ) : (
                                <div className="exception-properties">
                                    {exceptionData.properties.map((prop) => (
                                        <div key={prop.propertyId} className="exception-property-card">
                                            <div className="property-card-header">
                                                <div className="property-info">
                                                    <h3>{prop.propertyName}</h3>
                                                    {prop.propertyCode && <span className="property-code">{prop.propertyCode}</span>}
                                                </div>
                                                <div className="property-stats">
                                                    <span className="stat-pill submitted-pill">
                                                        {prop.submittedCount} / {prop.totalWorkdays} submitted
                                                    </span>
                                                    <span className="stat-pill missed-pill">
                                                        {prop.missedCount} missed
                                                    </span>
                                                </div>
                                            </div>
                                            <table className="exception-table">
                                                <thead>
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Day</th>
                                                        <th>Status</th>
                                                        <th>Reason</th>
                                                        <th>Provided By</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {prop.missedDays.map((day) => (
                                                        <tr key={day.date} className={day.hasReason ? 'has-reason' : 'no-reason'}>
                                                            <td>{new Date(day.date).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' })}</td>
                                                            <td>{day.dayName}</td>
                                                            <td>
                                                                {day.hasReason ? (
                                                                    <span className="status-badge excused-badge">Excused</span>
                                                                ) : (
                                                                    <span className="status-badge missed-badge">Missed</span>
                                                                )}
                                                            </td>
                                                            <td className="reason-cell">{day.reason || <span className="no-reason-text">No reason provided</span>}</td>
                                                            <td>{day.reasonProvidedBy || '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            <MissedReasonModal
                isOpen={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedDay(null);
                }}
                date={selectedDay?.date}
                propertyId={selectedPropertyId}
                propertyName={selectedProperty?.propertyName || ''}
                existingReason={selectedDay?.missedReason}
                providedByUserName={selectedDay?.missedReasonProvidedByUserName}
                providedDate={selectedDay?.missedReasonProvidedDate}
                onSave={saveMissedReason}
            />
        </div>
    );
};

ChecklistReportingPage.propTypes = {
    initialTab: PropTypes.string,
    showExceptionTab: PropTypes.bool,
    showChecklistTab: PropTypes.bool
};

export default ChecklistReportingPage;
